/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-snapshot.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useApolloClient } from '@apollo/client/react';
import { Tabs } from '@mui/material';
import Tab from '@mui/material/Tab';
import React, { useEffect, useState } from 'react';

import CreateSnapshot from './create-snapshot';
import DeleteOrganisation from './delete-organisation';
import DeleteSnapshot from './delete-snapshot';
import OrganisationsGrid from './organisation-grid';
import RestoreSnapshot from './restore-snapshot';
import SnapshotsGrid from './snapshots-grid';
import { useTranslation, useBoolean } from 'hooks';
import { GET_SYSTEM_ORGANISATIONS } from 'queries/org-admin';
import { GET_SNAPSHOTS } from 'queries/snapshots';
import NotificationService from 'utils/notification';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';

import './styles.scss';

const AdminSnapshot = () => {
    const { t } = useTranslation();
    const client = useApolloClient();

    const [data, setData] = useState(null);
    const [snapshotsData, setSnapshotsData] = useState(null);
    const [error, setError] = useState(null);
    const [errorSnapshots, setErrorSnapshots] = useState(null);

    const loading = useBoolean(false);
    const loadingSnapshots = useBoolean(false);

    const getSnapshotsData = () => {
        loadingSnapshots.setTrue();
        client
            .query({
                query: GET_SNAPSHOTS,
                fetchPolicy: 'network-only',
            })
            .then((res: any) => {
                loadingSnapshots.setFalse();
                if (res.data) setSnapshotsData(res.data);
                else if (res.error) setErrorSnapshots(res.error);
            })
            .catch((err: any) => {
                loadingSnapshots.setFalse();
                setErrorSnapshots(err);
            });
    };

    const getSystemOrganisations = () => {
        loading.setTrue();
        client
            .query({
                query: GET_SYSTEM_ORGANISATIONS,
                fetchPolicy: 'network-only',
            })
            .then((res: any) => {
                loading.setFalse();
                if (res.data) setData(res.data);
                else if (res.error) setError(res.error);
            })
            .catch((err: any) => {
                loading.setFalse();
                setError(err);
            });
    };

    const [value, setValue] = useState(0);
    const [organisations, setOrganisations] = useState([]);
    const [snapshots, setSnapshots] = useState([]);
    const [currentOrganisation, setCurrentOrganization] = useState(null);
    const [currentSnapshot, setCurrentSnapshot] = useState(null);

    const openCreateSnapshot = useBoolean(false);
    const openDeleteOrganisation = useBoolean(false);
    const openRestoreSnapshot = useBoolean(false);
    const openDeleteSnapshot = useBoolean(false);

    useEffect(() => {
        getSnapshotsData();
        getSystemOrganisations();
    }, []);

    useEffect(() => {
        if (data?.systemOrganisations) setOrganisations(data?.systemOrganisations);
    }, [data]);

    useEffect(() => {
        if (snapshotsData?.snapshots)
            setSnapshots(snapshotsData.snapshots.map((snapshot: any) => ({ name: snapshot })));
    }, [snapshotsData]);

    if (loading.value || loadingSnapshots.value) return <Loading float />;

    if (error || errorSnapshots) return <Error error={error || errorSnapshots} />;

    return (
        <>
            <DeleteOrganisation
                open={openDeleteOrganisation.value}
                onClose={() => {
                    openDeleteOrganisation.setFalse();
                    setCurrentOrganization(null);
                    getSystemOrganisations();
                }}
                organisations={organisations}
                orgName={currentOrganisation?.name}
                orgKey={currentOrganisation?.key}
            />
            <DeleteSnapshot
                open={openDeleteSnapshot.value}
                onClose={(action?: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'success') {
                            NotificationService.showNotification(
                                t('admin:snapshotHasBeeSuccessfullyDeleted', { name: currentSnapshot?.name }),
                            );
                            getSnapshotsData();
                        }
                        setCurrentSnapshot(null);
                        openDeleteSnapshot.setFalse();
                    }
                }}
                snapshots={snapshots}
                snapshotName={currentSnapshot?.name}
            />
            <RestoreSnapshot
                open={openRestoreSnapshot.value}
                onClose={(action?: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'success') {
                            getSystemOrganisations();
                            NotificationService.showNotification(
                                t('admin:snapshotHasBeenSuccessfullyRestored', { name: currentSnapshot?.name }),
                            );
                        }
                        setCurrentSnapshot(null);
                        openRestoreSnapshot.setFalse();
                    }
                }}
                organisations={organisations}
                snapshotName={currentSnapshot?.name}
            />
            <CreateSnapshot
                open={openCreateSnapshot.value}
                onClose={(action?: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'success') {
                            getSnapshotsData();
                            NotificationService.showNotification(t('admin:snapshotSuccessfullyCreated'));
                        }
                        openCreateSnapshot.setFalse();
                        setCurrentOrganization(null);
                    }
                }}
                orgKey={currentOrganisation?.key}
            />
            <PageTitle text={t('admin:manageSnapshots')} />
            <br />
            <Tabs
                value={value}
                onChange={(e, newValue) => setValue(newValue)}
                indicatorColor="primary"
                textColor="primary"
                centered
                style={{ marginBottom: 15 }}
            >
                <Tab label="Create" className="format-tab" data-testid="create-tab" />
                <Tab label="Restore" className="format-tab" data-testid="restore-tab" />
            </Tabs>
            {value === 0 ? (
                <OrganisationsGrid
                    organisations={organisations}
                    rowActions={(organisation: any) => [
                        {
                            label: 'admin:createSnapshot',
                            icon: ['fal', 'edit'],
                            onClick: () => {
                                setCurrentOrganization(organisation);
                                openCreateSnapshot.setTrue();
                            },
                            enabled: true,
                        },
                        {
                            label: 'admin:deleteOrganisation',
                            icon: ['fal', 'trash'],
                            onClick: () => {
                                setCurrentOrganization(organisation);
                                openDeleteOrganisation.setTrue();
                            },
                            enabled: true,
                        },
                    ]}
                />
            ) : (
                <SnapshotsGrid
                    snapshots={snapshots}
                    rowActions={(snapshot: any) => [
                        {
                            label: 'admin:restoreSnapshot',
                            icon: ['fal', 'edit'],
                            onClick: () => {
                                setCurrentSnapshot(snapshot);
                                openRestoreSnapshot.setTrue();
                            },
                            enabled: true,
                        },
                        {
                            label: 'admin:deleteSnapshot',
                            icon: ['fal', 'trash'],
                            onClick: () => {
                                setCurrentSnapshot(snapshot);
                                openDeleteSnapshot.setTrue();
                            },
                            enabled: true,
                        },
                    ]}
                />
            )}
        </>
    );
};

export default AdminSnapshot;
